import { HttpErrorResponse } from '@angular/common/http';
import { Component, ElementRef, HostListener, OnInit, ViewChild } from '@angular/core';
import { ToastrService } from 'ngx-toastr';
import { getLongDate, getStringDate } from 'src/app/common/util';
import { ButtonLoading } from 'src/app/models/button-loading.model';
import { EntryNote } from 'src/app/models/entry-note';
import { SimplePaginationModel } from 'src/app/models/pagination.model';
import { PurchaseOrderDetailResponse } from 'src/app/models/purchase-order.model';
import { ResponseObject } from 'src/app/models/response-object.model';
import { EntryNoteAssociationService } from 'src/app/services/entry-note-association.service';
import { EntryNoteService } from 'src/app/services/entry-note.service';
import { PurchaseOrderService } from 'src/app/services/purchase-order.service';
import { PurchasesAssociationService } from 'src/app/services/purchases-association.service';
import { PurchasesService } from 'src/app/services/purchases.service';

type PurchaseVM = {
  id: number;
  number: string;
  providerName: string;
  date: number | string;
  total: number;
  // associatedOrderId?: number | null;
};

type EntryNoteVM = {
  id: number;
  number: string;
  providerName: string;
  date: string;
};

@Component({
  selector: 'app-entrynote-purchase-association',
  templateUrl: './entrynote-purchase-association.component.html',
  styleUrls: ['./entrynote-purchase-association.component.scss']
})
export class EntrynotePurchaseAssociationComponent implements OnInit {
  loading = false;

  allPurchases: PurchaseVM[] = [];
  allEntryNotes: EntryNoteVM[] = [];

  purchases: PurchaseVM[] = [];
  entryNotes: EntryNoteVM[] = [];

  filterPurchases = '';
  filterEntryNotes = '';

  startDate = '';
  endDate = '';

  selectedEntryNoteId: number | null = null;
  selectedPurchaseId: number | null = null; // 👈 solo una factura por nota de entrada

  buttonAssociate: ButtonLoading = new ButtonLoading('btn-success', false, 'GUARDAR', 'fa-save');

  paginationPurchases = new SimplePaginationModel(0, 5, 0, 0, 0);
  paginationEntryNotes = new SimplePaginationModel(0, 5, 0, 0, 0);

  currentPurchases = [];
  currentEntryNote: EntryNote | null = null;
  currentRelatedId: number | null = null;
  basePurchases: PurchaseVM[] = [];
  loadingPurchaseDetails = false;

  // SVG
    @ViewChild('svgContainer', { static: true }) svgContainer: ElementRef;
    @HostListener('window:resize') onResize() { this.drawMultipleLinks(); }
  
    private timeoutPurchases: any = null;
    private timeoutNotes: any = null;

  constructor(
    private purchasesAssociationService: PurchasesAssociationService,
    private entryNoteAssociationService: EntryNoteAssociationService,
    private entryNotesService: EntryNoteService,
    private purchaseOrderService: PurchaseOrderService,
    private toastService: ToastrService,
    private purchasesService: PurchasesService
  ) {
    const today = new Date();
        this.startDate = getStringDate(today);
        this.endDate = getStringDate(today);
  }

  ngOnInit(): void {
    this.loadStartData();
  }

  /** Carga inicial */
  loadStartData() {
    this.loadEntryNotes();
    this.reloadPurchases();
  }

  private normalizeEntryNote(en: EntryNote): EntryNoteVM {
    return {
      id: en.id,
      number: String(en.noteNumber),
      providerName: en.providerData?.name ?? '—',
      date: en.entryDate+"",
    };
  }

  private normalizePurchase(p: any): PurchaseVM {
    const composedNumber = p.series ? `${p.series}-${p.number}` : String(p.number);
    return {
      id: p.id,
      number: composedNumber,
      providerName: p.providerData?.name ?? '—',
      date: p.date ?? p.accountingDate,
      total: p.totalCost ?? 0,
      // associatedOrderId: p.associatedOrderId ?? null
    };
  }

  /** Cargar notas de entrada */
  loadEntryNotes() {
    this.entryNotesService.getEntryNotesAll().subscribe({
      next: (resp: ResponseObject) => {
        this.entryNotes = (resp.data || []).map((en: any) => this.normalizeEntryNote(en));
        this.allEntryNotes = this.entryNotes;
        this.refreshEntryNotesPagination();
      },
      error: () => this.toastService.error('Error al obtener notas de entrada')
    });
  }

  /** Seleccionar una EntryNote */
  selectEntryNote(entryNoteId: number) {
    if (this.selectedEntryNoteId === entryNoteId) return;
    
    this.selectedEntryNoteId = entryNoteId;
    this.selectedPurchaseId = null;

    this.entryNotesService.getEntryNoteById(entryNoteId).subscribe({
      next: (resp: ResponseObject) => {
        this.currentEntryNote = resp.data;
        this.selectedPurchaseId = this.currentEntryNote?.providerInvoiceId || null;
        this.currentRelatedId = this.selectedPurchaseId;

        this.paginationPurchases.page = 0;
        this.refreshPurchasesPagination();
        this.getPurchaseDetails();
        this.drawMultipleLinks();
      },
      error: () => {
        this.currentEntryNote = null;
        this.toastService.error('Error al obtener detalles de la nota de entrada');
      }
    });
  }

  /** Seleccionar una factura (uno a uno) */
  togglePurchase(p: PurchaseVM) {
    
    if (!this.selectedEntryNoteId) return;
    
    if (this.isPurchaseLocked(p)) {
      this.toastService.warning('Esta compra ya está asociada a otra factura.');
      return;
    }

    const idx = this.selectedPurchaseId;
    if (idx != null) {
      if (idx === p.id) {
        this.selectedPurchaseId = null;
        this.currentRelatedId = null;
      } else {
        this.selectedPurchaseId = p.id;
        this.currentRelatedId = p.id;
      }
    } else {
      this.currentRelatedId = p.id;
      this.selectedPurchaseId = p.id;
    }
    this.getPurchaseDetails();
    this.refreshPurchasesPagination();
    this.drawMultipleLinks();
  }

  /** Regla: una factura solo puede estar asociada a UNA entry note */
  isPurchaseLocked(p: PurchaseVM): boolean {
    // if (!p.associatedOrderId) return false;
    // // Está asociada a otra factura distinta a la seleccionada
    // return this.selectedPurchaseId != null && p.associatedOrderId !== this.selectedPurchaseId;
    return false;
  }

  getDisabledSave(): boolean {
    return !this.selectedEntryNoteId || !this.selectedPurchaseId || this.buttonAssociate.loading;
  }

  /** Guardar la asociación */
  saveAssociation() {
    
    if (!this.selectedEntryNoteId || !this.selectedPurchaseId) return;

    if (this.selectedPurchaseId == this.currentEntryNote.providerInvoiceId) {
      this.toastService.info('Esta es la asociacion actual de la Nota de Ingreso', 'Info');
      return;
    } else {
      this.buttonAssociate.loading = true;
      this.save();
      // this.entryNotesService.isExisteOtherAssociationEntrynoteOrder(this.selectedEntryNoteId, this.selectedPurchaseId).subscribe({
      //   next: (response: ResponseObject) => {
      //     if(!response.data) {
      //       this.save();
      //     } else {
      //       this.toastService.error("Esta Factura ya tiene una asociacion a otra nota de entrada", "Error")
      //       this.buttonAssociate.loading = false;
      //     }
      //   }, 
      //   error: (error: HttpErrorResponse) => {
      //     this.toastService.error("Error al verificar la existencia de la asociation", "Error")
      //     this.buttonAssociate.loading = false;
      //   }
      // });
    }

    
  }

  save() {
    this.entryNoteAssociationService.associateEntryNoteToInvoice(
      this.selectedEntryNoteId,
      this.selectedPurchaseId
    ).subscribe({
      next: () => {
        this.toastService.success('Asociación actualizada correctamente', 'Éxito');
        this.buttonAssociate.loading = false;
        this.selectEntryNote(this.selectedEntryNoteId!);
        this.reloadPurchases();
        this.buttonAssociate.loading = false;
      },
      error: (e: HttpErrorResponse) => {
        this.toastService.error(e?.error?.message || 'Error al actualizar asociación', 'Error');
        this.buttonAssociate.loading = false;
      }
    });
  }

  /** Obtener detalles de la factura seleccionada */
  getPurchaseDetails() {
    this.currentPurchases = [];

    if (!this.selectedPurchaseId) {
      return;
    }

    this.loadingPurchaseDetails = true;

    this.purchasesService.getDetailPurchaseInvoice(this.selectedPurchaseId).subscribe({
      next: (resp: ResponseObject) => {
        this.currentPurchases = resp.data || [];
        this.loadingPurchaseDetails = false;
      },
      error: () => {
        this.toastService.error('Error al obtener detalles de la factura')
        this.loadingPurchaseDetails = false;
      }
      
    });
  }

  /** 🔄 Paginación de órdenes */
  refreshEntryNotesPagination() {
    const q = (this.filterEntryNotes || '').toLowerCase();
    const filtered = this.allEntryNotes.filter(en =>
      (en.number || '').toLowerCase().includes(q) ||
      (en.providerName || '').toLowerCase().includes(q)
    );

    this.paginationEntryNotes.total = filtered.length;
    this.paginationEntryNotes.start = this.paginationEntryNotes.page * this.paginationEntryNotes.size;
    const end = this.paginationEntryNotes.start + this.paginationEntryNotes.size;
    this.paginationEntryNotes.end = Math.min(end, filtered.length);

    this.entryNotes = filtered.slice(this.paginationEntryNotes.start, this.paginationEntryNotes.end);
    this.drawMultipleLinks();
  }

  /** 🔄 Recargar órdenes */
  reloadPurchases() {
    const start = getLongDate(this.startDate);
    const end = getLongDate(this.endDate);

    this.purchasesService.getPurchasesByIntervalDate(start, end).subscribe({
      next: (resp: ResponseObject) => {
        this.basePurchases = (resp.data || []).map((p: any) => this.normalizePurchase(p));
        this.resetAllPurchasesToBase();
        
        // if (this.selectedOrderId && this.currentRelatedPurchaseIds?.length) {
        //   const cached = this.associatedPurchasesCache.get(this.selectedOrderId) || [];
        //   if (cached.length) this.upsertAssociatedIntoAllPurchases(cached, this.selectedOrderId);

        //   const relatedSet = new Set(this.currentRelatedPurchaseIds);
        //   this.allPurchases = this.allPurchases.map(p => ({
        //     ...p,
        //     associatedOrderId: relatedSet.has(p.id) ? this.selectedOrderId : p.associatedOrderId ?? null
        //   }));
        // }

        this.paginationPurchases.page = 0;
        this.refreshPurchasesPagination();
        this.drawMultipleLinks();
      },
      error: () => this.toastService.error('Error al obtener compras')
    });
  }

  private resetAllPurchasesToBase() {
    this.allPurchases = this.basePurchases.map(p => ({ ...p }));
  }

  refreshPurchasesPagination() {
    const q = (this.filterPurchases || '').toLowerCase();
    const matches = (p: PurchaseVM) =>
      (p.number || '').toLowerCase().includes(q) ||
      (p.providerName || '').toLowerCase().includes(q);

    const associatedSet = new Set();
    associatedSet.add(this.currentEntryNote?.providerInvoiceId);
    
    const pinned = this.allPurchases.filter(p => associatedSet.has(p.id));

    const restRaw = this.allPurchases.filter(p => !associatedSet.has(p.id) && matches(p));

    const score = (x: PurchaseVM) => {
      if (x.id == this.selectedEntryNoteId) return 1;
      return 2;                               
    };
    const rest = restRaw.sort((a, b) => score(a) - score(b));

    const final = [...pinned, ...rest];

    this.paginationPurchases.total = final.length;
    this.paginationPurchases.start = this.paginationPurchases.page * this.paginationPurchases.size;
    const end = this.paginationPurchases.start + this.paginationPurchases.size;
    this.paginationPurchases.end = Math.min(end, final.length);

    this.purchases = final.slice(this.paginationPurchases.start, this.paginationPurchases.end);

    this.drawMultipleLinks();
  }

  debouncedFilterNotes() {
    clearTimeout(this.timeoutNotes);
    this.timeoutNotes = setTimeout(() => {
      this.paginationEntryNotes.page = 0;
      this.refreshEntryNotesPagination();
    }, 250);
  }

  debouncedFilterPurchases() {
    clearTimeout(this.timeoutPurchases);
    this.timeoutPurchases = setTimeout(() => {
      this.paginationPurchases.page = 0;
      this.refreshPurchasesPagination();
    }, 250);
  }

  /** Dibuja líneas desde la factura seleccionada hacia cada compra actualmente “seleccionada” */
  drawMultipleLinks() {
    const svg = this.svgContainer?.nativeElement as SVGElement;
    if (!svg) return;

    svg.innerHTML = '';
    
    if (!this.selectedPurchaseId || !this.selectedEntryNoteId) return;
    setTimeout(() => {
      const parentRect = (svg.parentElement as HTMLElement).getBoundingClientRect();
      const selectedRow = document.querySelector('#table-notes tr.selected') as HTMLElement;
      if (!selectedRow) return;

      const selectedRect = selectedRow.getBoundingClientRect();
      const x1 = selectedRect.right - parentRect.left - 12;
      const y1 = selectedRect.top + selectedRect.height / 2 - parentRect.top;

      if (!this.currentRelatedId) return;
      const pid = this.selectedPurchaseId;
      
      const relatedRow = document.querySelector(`#table-purchases tr[data-id="${pid}"]`) as HTMLElement;
      if (!relatedRow) return;

      const rect = relatedRow.getBoundingClientRect();
      const x2 = rect.left - parentRect.left - 12;
      const y2 = rect.top + rect.height / 2 - parentRect.top;
      const xMiddle = x1 + ((x2 - x1) / 2);

      this.drawLine(svg, x1, xMiddle, y1, y1);
      this.drawLine(svg, xMiddle, x2, y2, y2);
      this.drawLine(svg, xMiddle, xMiddle, y1, y2);

    }, 50);
  }

  private drawLine(svg: SVGElement, x1: number, x2: number, y1: number, y2: number) {
    const line = document.createElementNS('http://www.w3.org/2000/svg', 'line');
    line.setAttribute('x1', String(x1));
    line.setAttribute('y1', String(y1));
    line.setAttribute('x2', String(x2));
    line.setAttribute('y2', String(y2));
    line.setAttribute('stroke', '#001f54');
    line.setAttribute('stroke-width', '2');
    svg.appendChild(line);
  }
}